import React from 'react';
import {
  DocPage, PageHeader, Section, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './RateCard.css';

const TOC = [
  { id: 'overview',    label: 'Overview' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'boundaries',  label: 'Billing period boundaries' },
  { id: 'priority',    label: 'Priority order' },
  { id: 'multiple',    label: 'Multiple overrides' },
  { id: 'unchanged',   label: 'What does not change' },
];

function RcTable({ headers, rows }) {
  return (
    <div className="rc-table-wrap">
      <table className="rc-table">
        <thead>
          <tr>{headers.map((h) => <th key={h} className="rc-th">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => <td key={j} className="rc-td">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RateCardTimeBased({ onNavigate }) {
  const { industry } = useIndustry();
  const tb = industry.rateCard.timeBased;
  const description = `A time-based rate card is a date-scoped pricing override — it replaces a rate card's standard pricing during its active window and expires automatically when the window closes.`;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Billing · Rate Card"
        title="Time-Based Rate Cards"
        description={description}
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'rate-card',      label: 'Rate Cards' },
          { id: 'pricing-models', label: 'Pricing Models' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          Each rate card is defined by three things: a fee, the
          applicable pricing dimensions (e.g., Zone A, Brand 1), and a currency. Together
          these form a <em>rate card slot</em>. A slot has a standard pricing rule that
          applies by default.
        </Para>
        <Para>
          A time-based rate card lets you define an alternate pricing rule for a specific
          date range without editing the original rate card or touching any contracts.
        </Para>
        <Para>
          Common use cases: seasonal discounts, promotional pricing campaigns, temporary
          price increases, or rate revision periods while a contract renegotiation is pending.
        </Para>
        <RcTable
          headers={['Slot', 'Standard rate', 'Time-based override', 'Active window']}
          rows={[[
            tb.slot,
            tb.standardRateLabel,
            tb.override.rateLabel,
            tb.override.window,
          ]]}
        />
        <Para>
          During the override window, any usage on{' '}
          <strong>{tb.feeComponent}</strong> is priced at{' '}
          <strong>{tb.override.rateLabel}</strong>. When the window closes, the rate
          reverts to <strong>{tb.standardRateLabel}</strong> automatically — no manual
          intervention needed.
        </Para>
      </Section>

      <Section id="how-it-works" title="How it works">
        <Para>
          A time-based rate card is attached to an existing rate card slot. It carries:
        </Para>
        <RcTable
          headers={['Field', 'Description']}
          rows={[
            ['Start date', 'The first day the override applies (inclusive)'],
            ['End date',   'The last day the override applies (inclusive)'],
            ['Pricing rule', 'A full rate card definition — model type, rates, tiers, currency — identical in structure to any standard rate card'],
          ]}
        />
        <Para>
          Outside its active window, a time-based rate card is invisible to the billing
          engine. It does not affect any invoice generated for periods that fall entirely
          outside the window.
        </Para>
      </Section>

      <Section id="boundaries" title="Billing period boundaries">
        <Para>
          The billing engine looks at the date the billing period <strong>ends</strong>{' '}
          to determine which rate applies. If the billing period ends within the time-based
          window, the time-based rate applies for the entire period. If it ends after the
          window, the standard rate applies.
        </Para>
        <RcTable
          headers={['Billing period', 'Window', 'Rate applied', 'Reason']}
          rows={tb.periodTable.map((r) => [
            r.period,
            tb.override.window,
            <span key="r" style={{ fontWeight: r.active ? 700 : undefined }}>{r.rate}</span>,
            r.reason,
          ])}
        />
        <Callout type="info">
          The period-end rule keeps billing simple and deterministic: each invoice
          resolves to exactly one rate card. There is no mid-period splitting.
        </Callout>
      </Section>

      <Section id="priority" title="Priority order">
        <Para>
          The billing engine resolves a rate card in two steps:
        </Para>
        <Para>
          <strong>Step 1 — Select the base rate card.</strong> A rate card slot can hold
          multiple <em>variants</em> — alternate pricing rules for the same slot (e.g., a
          standard rate and a negotiated rate). One is marked as the default. The billing
          engine uses the contract's assigned variant if one is set, otherwise the default.
        </Para>
        <Para>
          <strong>Step 2 — Check for a time-based version.</strong> If the selected base
          rate card has a time-based override whose window covers the billing period end
          date, that time-based version is used instead. If it does not, the base rate
          card is used as-is.
        </Para>
        <Para>
          A time-based override only applies to the rate card it is attached to.
          If a contract uses a negotiated variant and that variant has no time-based
          version, the negotiated rate applies unchanged — even if the default variant
          has a time-based override active for the same period.
        </Para>
        <RcTable
          headers={['Rate card', 'Type', 'Rate', 'Time-based window']}
          rows={[
            ['RC_001', 'Default',   tb.standardRateLabel, `${tb.override.window} → ${tb.override.rateLabel}`],
            ['RC_002', `${tb.variant.name}`, tb.variant.rateLabel, '(none)'],
          ]}
        />
        <Para>
          During the override window:
        </Para>
        <RcTable
          headers={['Customer contract', 'Rate applied', 'Why']}
          rows={[
            ['On RC_001 (default)',          tb.override.rateLabel,  'Time-based version of RC_001 is active'],
            [`On RC_002 (${tb.variant.name})`, tb.variant.rateLabel, 'RC_002 has no time-based version'],
          ]}
        />
      </Section>

      <Section id="multiple" title="Multiple overrides">
        <Para>
          A rate card slot can have multiple time-based rate cards, as long as their date
          windows do not overlap. Each window gets its own pricing rule independently.
          Overlapping windows are not permitted — the system will reject a time-based rate
          card whose dates conflict with an existing one on the same slot.
        </Para>
      </Section>

      <Section id="unchanged" title="What does not change">
        <RcTable
          headers={['Aspect', 'Behavior']}
          rows={[
            ['Contracts',          'No contract needs to be updated when a time-based rate card is created or expires. The override applies automatically to all active contracts on that slot.'],
            ['Rate card variants', 'The time-based rate card sits above the variant layer. When it expires, the contract\'s assigned variant (or the default) resumes exactly as before.'],
            ['Historical invoices','Time-based rate cards only apply to billing periods whose end date falls within their window. Invoices already issued are never recalculated.'],
            ['Proration',          'If a rate card has proration enabled, the time-based override inherits that behavior. The prorated fraction is applied to the time-based rate, not the original.'],
          ]}
        />
      </Section>

    </DocPage>
  );
}
